import { useState, useRef, useEffect } from "react";
import "./styles.css";
import { LuChevronDown } from "react-icons/lu";
import { useDispatch } from "react-redux";
import { addLanguage } from "../../features/CocoConfigurationSlice";
import PropTypes from "prop-types";

const CustomSelect = ({
  options = [],
  placeHolder = "Select an option",
  onChange,
  label = "",
  variant = "",
  defaultValue = null,
  value = null,
  name = "",
  onToggle,
  className = "",
  disabled = false,
  onBlur: onBlurProp,
  enableTypeahead = false,
  dispatchValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  // keep component controlled by parent value/defaultValue to avoid internal loop
  // selected is derived from props (value or defaultValue) instead of internal state
  // internal selection state removed to prevent update loops
  const selectRef = useRef(null);
  const optionsRef = useRef([]);
  const dispatch = useDispatch();
  // Generate unique id for accessibility and keep it stable across renders
  const selectIdRef = useRef(null);
  if (!selectIdRef.current)
    selectIdRef.current = "custom-select-" + crypto.randomUUID();
  const selectId = selectIdRef.current;

  // typeahead (type-to-select) buffer & timer
  const [typeahead, setTypeahead] = useState("");
  const typeaheadTimerRef = useRef(null);

  const clearTypeahead = () => {
    setTypeahead("");
    if (typeaheadTimerRef.current) {
      clearTimeout(typeaheadTimerRef.current);
      typeaheadTimerRef.current = null;
    }
  };

  const handleBlur = (e) => {
    if (selectRef.current && !selectRef.current.contains(e.relatedTarget)) {
      setIsOpen(false);
      setHighlightedIndex(-1);
      if (onBlurProp) {
        onBlurProp(e);
      }
    }
  };

  const toggleOpen = () => {
    // notify parent of toggle (mimic native select click event shape)
    if (onToggle) {
      onToggle({ target: { name } });
    }
    if (isOpen) {
      setIsOpen(false);
      setHighlightedIndex(-1);
      return;
    }
    const selectedIndex = options?.length
      ? Math.max(
          0,
          options.findIndex((o) => o.value === currentValue)
        )
      : 0;
    setHighlightedIndex(selectedIndex);
    setIsOpen(true);
  };

  // derive selected option from controlled props
  // normalize incoming value/defaultValue: allow either primitive value or an option object
  const currentValue =
    (value && (typeof value === "object" ? value.value : value)) ||
    (defaultValue &&
      (typeof defaultValue === "object" ? defaultValue.value : defaultValue));

  const selectedOption = options?.find((o) => o.value === currentValue);

  const selectOption = (option) => {
    if (onChange) onChange(option);
    if (dispatchValue) dispatch(addLanguage(option?.value ?? option));
    setIsOpen(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    // Typeahead: enable only for header-form-select or when explicitly allowed
    const allowTypeahead =
      +enableTypeahead || className?.includes("header-form-select");
    const isPrintable =
      e.key && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

    if (allowTypeahead && isPrintable) {
      // accumulate typed characters
      const nextBuffer = (typeahead + e.key).toLowerCase();
      setTypeahead(nextBuffer);
      if (typeaheadTimerRef.current) clearTimeout(typeaheadTimerRef.current);
      typeaheadTimerRef.current = setTimeout(() => clearTypeahead(), 800);

      // find matching option by label startingWith buffer
      const matchIndex = options.findIndex((o) =>
        (o.label || "")?.toLowerCase().startsWith(nextBuffer)
      );

      // fallback: single-character search
      let finalIndex = matchIndex;
      if (finalIndex === -1) {
        const single = e.key.toLowerCase();
        finalIndex = options.findIndex((o) =>
          (o.label || "")?.toLowerCase().startsWith(single)
        );
      }

      if (finalIndex >= 0) {
        if (isOpen) {
          setHighlightedIndex(finalIndex);
        } else {
          // mimic native select: change selection when closed
          selectOption(options[finalIndex]);
        }
      }
      e.preventDefault();
      return;
    }

    if (!isOpen) {
      if (["Enter", "ArrowDown", " "].includes(e.key)) {
        e.preventDefault();
        const selectedIndex = selectedOption
          ? options.findIndex((o) => o.value === selectedOption.value)
          : 0;
        setHighlightedIndex(selectedIndex);
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % options.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(
          (prev) => (prev - 1 + options.length) % options.length
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          selectOption(options[highlightedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && optionsRef.current[highlightedIndex]) {
      optionsRef.current[highlightedIndex].scrollIntoView({
        block: "nearest",
      });
    }
  }, [highlightedIndex]);

  // Auto-select on mount or when defaultValue changes (do not call parent's onChange here
  // because that may trigger parent state updates and cause an update loop)
  // No internal selected state — parent controls selection via `value`/`defaultValue`.

  // ... all your existing state & handlers ...

  let styleClassName = "select-trigger";
  if (variant === "select-secondary") {
    styleClassName = "select-trigger select-secondary";
  } else if (variant === "select-search") {
    styleClassName = "select-trigger select-search";
  } else if (variant === "select-coco") {
    styleClassName = "select-trigger select-coco";
  }

  return (
    <div className={`custom-select-wrapper ${className}`}>
      {label && (
        <label htmlFor={selectId + "-button"} className="custom-select-label">
          {label}
        </label>
      )}

      <div className="custom-select" ref={selectRef} onBlur={handleBlur}>
        <button
          type="button"
          id={selectId + "-button"}
          className={`${styleClassName} ${className || ""}`}
          onClick={toggleOpen}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={selectId + "-listbox"}
          aria-labelledby={selectId + "-label " + selectId + "-button"}
          aria-activedescendant={
            highlightedIndex >= 0
              ? `${selectId}-option-${highlightedIndex}`
              : undefined
          }
          onKeyDown={handleKeyDown}
          disabled={disabled}
        >
          <span
            className={`selected-value ${selectedOption ? "" : "placeholder"}`}
          >
            {selectedOption ? selectedOption.label : placeHolder}
          </span>

          <span
            className={`chevron ${isOpen ? "open" : ""}`}
            aria-hidden="true"
          >
            <LuChevronDown size={22} strokeWidth={2} />
          </span>
        </button>

        {isOpen && (
          <ul
            className="options-list"
            role="listbox"
            id={selectId + "-listbox"}
            tabIndex={-1}
          >
            {options.map((option, idx) => (
              <li
                key={option.value}
                id={`${selectId}-option-${idx}`}
                role="option"
                aria-selected={selectedOption?.value === option.value}
                className={`option ${
                  selectedOption?.value === option.value ? "selected" : ""
                } ${highlightedIndex === idx ? "highlighted" : ""}`}
                onClick={() => selectOption(option)}
                ref={(el) => (optionsRef.current[idx] = el)}
                tabIndex={-1}
                onMouseEnter={() => setHighlightedIndex(idx)}
              >
                {option.label}
              </li>
            ))}
            {/* NOSONAR */}
          </ul>
        )}
      </div>
      {/* NOSONAR */}
    </div>
  );
};

CustomSelect.propTypes = {
  options: PropTypes.array,
  onBlur: PropTypes.func,
  placeHolder: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  variant: PropTypes.string,
  defaultValue: PropTypes.any,
  value: PropTypes.any,
  name: PropTypes.string,
  onToggle: PropTypes.func,
  className: PropTypes.string,
  dispatchValue: PropTypes.any,
};

export default CustomSelect;
